import { useQuery } from '@tanstack/react-query'
import type { UseQueryResult } from '@tanstack/react-query'
import api from '@/utils/api'
import { useState } from 'react'
import type { DriverTrackingResponse, QueryFilters } from '../types'
import type { AxiosResponse } from 'axios'

const DEFAULT_QUERY_FILTERS: QueryFilters = {
  shift_date: '',
  order_numbers: [] as number[],
  driver_names: [] as string[],
  driver_ids: [] as string[],
}

export function useDriverTracking({ disablePolling } = { disablePolling: false }) {
  const [activeQueryFilters, setActiveQueryFilters] = useState(DEFAULT_QUERY_FILTERS)

  const pointsOfInterestQuery = useQuery({
    queryKey: ['driverTracking::pointsOfInterest'],
    queryFn: () => api.getPointsOfInterest(),
  })

  const allDriversQuery: UseQueryResult<AxiosResponse<DriverTrackingResponse>, Error> = useQuery({
    queryKey: ['driverTracking::all', activeQueryFilters.shift_date],
    queryFn: () =>
      api.getAllDriverTracking({
        shift_date: activeQueryFilters.shift_date,
      }),
    refetchInterval: !disablePolling && 60_000, // Refetch every minute
    enabled: !!activeQueryFilters?.shift_date,
  })

  const driverSearchQuery: UseQueryResult<AxiosResponse<DriverTrackingResponse>, Error> = useQuery({
    queryKey: ['driverTracking::search', activeQueryFilters],
    queryFn: () => {
      if (activeQueryFilters?.order_numbers?.length > 0) {
        return api.getAllDriverTracking({ order_numbers: activeQueryFilters.order_numbers })
      }
      return api.getAllDriverTracking({
        shift_date: activeQueryFilters.shift_date,
        driver_names: activeQueryFilters.driver_names,
      })
    },
    refetchInterval: !disablePolling && 60_000,
    enabled: activeQueryFilters?.driver_names?.length > 0 || activeQueryFilters?.order_numbers?.length > 0,
  })

  const singleDriverQuery: UseQueryResult<AxiosResponse<DriverTrackingResponse>, Error> = useQuery({
    queryKey: ['driverTracking::single', activeQueryFilters.driver_ids],
    queryFn: () =>
      api.getAllDriverTracking({
        shift_date: activeQueryFilters.shift_date,
        driver_ids: activeQueryFilters.driver_ids,
      }),
    refetchInterval: !disablePolling && 20_000, // Refetch every 20 seconds for individual drivers
    enabled: activeQueryFilters?.driver_ids?.length > 0 && !!activeQueryFilters?.shift_date,
  })

  return {
    pointsOfInterestQuery,
    allDriversQuery,
    driverSearchQuery,
    singleDriverQuery,
    activeQueryFilters,
    setActiveQueryFilters,
  }
}
